import { DailyDataResponse } from '../types';
import { generateTimeLabel, convertPostureToNumber } from './dailyChartUtils';

export type ChartDataPoint = {
  time: string;
  value: number | string;
  index: number;
};

export type OverlayChartDataPoint = {
  time: string;
  index: number;
  sleepStage?: number;
  noise?: number;
  posture?: number;
  temperature?: number;
};

// 睡眠ステージのデータを準備
export const prepareSleepStageData = (data: DailyDataResponse): ChartDataPoint[] => {
  const startTime = new Date(data.start_time);
  return data.sleep_stages.map((stage, index) => ({
    time: generateTimeLabel(startTime, 2, index),
    value: stage,
    index
  }));
};

// ノイズレベルのデータを準備（10秒間隔を2分間隔に変換）
export const prepareNoiseData = (data: DailyDataResponse): ChartDataPoint[] => {
  const startTime = new Date(data.start_time);
  const noiseData: ChartDataPoint[] = [];
  
  for (let i = 0; i < data.noise_stages.length; i += 12) {
    const chunk = data.noise_stages.slice(i, i + 12);
    const average = chunk.reduce((sum, val) => sum + val, 0) / chunk.length;
    noiseData.push({
      time: generateTimeLabel(startTime, 2, Math.floor(i / 12)),
      value: Math.round(average * 10) / 10,
      index: Math.floor(i / 12)
    });
  }
  
  return noiseData;
};

// 姿勢データを準備
export const preparePostureData = (data: DailyDataResponse): ChartDataPoint[] => {
  const startTime = new Date(data.start_time);
  return data.posture_stages_aggregate.map((posture, index) => ({
    time: generateTimeLabel(startTime, 2, index),
    value: convertPostureToNumber(posture),
    index
  }));
};

// 温度データを準備
export const prepareTemperatureData = (data: DailyDataResponse): ChartDataPoint[] => {
  const startTime = new Date(data.start_time);
  return data.bed_temperature_aggregate.map((temp, index) => ({
    time: generateTimeLabel(startTime, 2, index),
    value: temp,
    index
  }));
};

// オーバーレイ用のデータを準備
export const prepareOverlayData = (
  sleepStageData: ChartDataPoint[],
  noiseData: ChartDataPoint[],
  postureData: ChartDataPoint[],
  temperatureData: ChartDataPoint[],
  selectedMetrics: {
    sleepStage: boolean;
    noise: boolean;
    posture: boolean;
    temperature: boolean;
  }
): OverlayChartDataPoint[] => {
  return sleepStageData.map((item, index) => {
    const point: OverlayChartDataPoint = {
      time: item.time,
      index: item.index
    };
    
    if (selectedMetrics.sleepStage) {
      point.sleepStage = item.value as number;
    }
    if (selectedMetrics.noise && noiseData[index]) {
      point.noise = noiseData[index].value as number;
    }
    if (selectedMetrics.posture && postureData[index]) {
      point.posture = postureData[index].value as number;
    }
    if (selectedMetrics.temperature && temperatureData[index]) {
      point.temperature = temperatureData[index].value as number;
    }
    
    return point;
  });
};
